import { msg } from '@lingui/core/macro';

import { defineEnum } from '@/shared/patterns/define-enum';

/**
 * Os oito estados do acordo (collection.types.ts:735-755), em português no fio. O rótulo segue o
 * `[Description]` do backend; a cor segue `agreementStatusColor` (collection.helpers.ts:252-258).
 */
export const AGREEMENT_STATUS = defineEnum({
  Rascunho: { label: msg`Rascunho`, tone: 'warn', icon: 'pencil' },
  Negociado: { label: msg`Negociado`, tone: 'warn', icon: 'handshake' },
  Formalizado: { label: msg`Formalizado`, tone: 'info', icon: 'file-check' },
  EmAndamento: { label: msg`Em andamento`, tone: 'info', icon: 'clock' },
  Cumprido: { label: msg`Cumprido`, tone: 'ok', icon: 'circle-check' },
  Quebrado: { label: msg`Quebrado`, tone: 'err', icon: 'circle-x' },
  Cancelado: { label: msg`Cancelado`, tone: 'neutral', icon: 'ban' },
  Substituido: { label: msg`Substituído`, tone: 'neutral', icon: 'circle-dashed' },
});

/* eslint-disable lingui/no-unlocalized-strings -- valores técnicos do fio, não texto de UI */
/** Estados terminais (collection.types.ts:765-773): terminal não volta. */
export const TERMINAL_AGREEMENT_STATUSES: readonly string[] = ['Cumprido', 'Quebrado', 'Cancelado', 'Substituido'];

/* eslint-enable lingui/no-unlocalized-strings */
export const isTerminalAgreementStatus = (status?: string | null) => !!status && TERMINAL_AGREEMENT_STATUSES.includes(status);

/** Acordo "concorrente" enquanto não chega a terminal (collection.types.ts:779). */
export const isActiveAgreement = (status?: string | null) => !isTerminalAgreementStatus(status);

/* eslint-disable lingui/no-unlocalized-strings -- valores técnicos do fio, não texto de UI */
/**
 * Cancelar só antes do pagamento (collection.types.ts:789-796): EmAndamento já recebeu, e a máquina não
 * abre Cancelado a partir dele.
 */
const CANCELLABLE: readonly string[] = ['Rascunho', 'Negociado', 'Formalizado'];
/** Renegociar cria um sucessor e leva este a Substituído; Cumprido não renegocia (collection.types.ts:805-812). */
const RENEGOTIABLE: readonly string[] = ['Formalizado', 'EmAndamento', 'Quebrado'];

/**
 * A matriz estado × ação do cabeçalho do acordo (AgreementFormHeader.tsx:98-163). EmAndamento, Cumprido e
 * Quebrado vêm de pagamento e vencimento: nenhuma ação leva a eles.
 */
export const agreementActionsFor = (status: string) => ({
  negotiate: status === 'Rascunho',
  formalize: status === 'Negociado',
  renegotiate: RENEGOTIABLE.includes(status),
  cancel: CANCELLABLE.includes(status),
  closed: isTerminalAgreementStatus(status),
});
/* eslint-enable lingui/no-unlocalized-strings */

/** Tipo de pessoa do devedor na fila (CollectionQueueFilters.tsx:30-33): o nome do `PersonTypeEnum`. */
/* eslint-disable lingui/no-unlocalized-strings -- valores técnicos do fio; o rótulo é msg */
export const PAYER_PERSON_TYPE = [
  { value: 'Natural', label: msg`Pessoa física` },
  { value: 'Legal', label: msg`Pessoa jurídica` },
] as const;

/** "Parcelas em acordo" (CollectionQueueFilters.tsx:17-20): o padrão do backend é `false`. */
export const INCLUDE_RENEGOTIATED = [
  { value: 'false', label: msg`Ocultar (padrão)` },
  { value: 'true', label: msg`Incluir parcelas em acordo` },
] as const;
/* eslint-enable lingui/no-unlocalized-strings */
